import { useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { Request } from '../../types';
import { bucketSolution } from './OfferingChart';
function generateColors(count: number): string[] {
  const goldenAngle = 137.508;
  return Array.from({
    length: count
  }, (_, i) => {
    const hue = i * goldenAngle % 360;
    return `hsl(${Math.round(hue)}, 65%, 50%)`;
  });
}
interface Props {
  requests: Request[];
  top: number;
  onTopChange: (v: number) => void;
  onTotalChange?: (v: number) => void;
  chartHeight?: number;
}
export function AccountBySolutionChart({
  requests,
  top,
  onTotalChange,
  chartHeight = 220
}: Props) {
  const solAccounts: Record<string, Set<string>> = {};
  const solSeenIds: Record<string, Set<string>> = {};
  requests.forEach(r => {
    const sol = bucketSolution(r.req_products || '');
    if (!solAccounts[sol]) solAccounts[sol] = new Set();
    if (!solSeenIds[sol]) solSeenIds[sol] = new Set();
    if (solSeenIds[sol].has(r.opp_id)) return;
    solSeenIds[sol].add(r.opp_id);
    if (r.acc_name) solAccounts[sol].add(r.acc_name);
  });
  const solSorted = Object.entries(solAccounts).map(([sol, accounts]) => [sol, accounts.size] as [string, number]).sort((a, b) => b[1] - a[1]);
  const totalBuckets = solSorted.length;
  useEffect(() => {
    onTotalChange?.(totalBuckets);
  }, [totalBuckets, onTotalChange]);
  let labels: string[], vals: number[];
  if (totalBuckets > top) {
    const topSlices = solSorted.slice(0, top);
    const othersCount = solSorted.slice(top).reduce((s, [, v]) => s + v, 0);
    labels = [...topSlices.map(([l]) => l), 'Others'];
    vals = [...topSlices.map(([, v]) => v), othersCount];
  } else {
    labels = solSorted.map(([l]) => l);
    vals = solSorted.map(([, v]) => v);
  }
  const data = labels.map((name, i) => ({
    name,
    value: vals[i]
  }));
  const colors = generateColors(data.length);
  const mid = Math.ceil(data.length / 2);
  const col1 = data.slice(0, mid);
  const col2 = data.slice(mid);
  const renderLegendCol = (items: typeof data) => <ul style={{
    listStyle: 'none',
    padding: 0,
    margin: 0
  }} data-id="element-80" data-mp="2t4jg9g3rmaxd">
      {items.map((item, i) => {
      const colorIdx = data.indexOf(item);
      return <li key={i} style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        marginBottom: 2,
        fontSize: 9,
        color: '#374151'
      }} data-id="element-81" data-mp="2f6urvyaholnk" data-mpi={i}>
            <span style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: colors[colorIdx],
          flexShrink: 0
        }} data-id="element-82" data-mp="37u7e27cqmrgc" />
            <span data-id="element-83" data-mp="3cuv4aqt5749i">{item.name} ({item.value})</span>
          </li>;
    })}
    </ul>;
  return <div style={{
    display: 'flex',
    alignItems: 'center'
  }} data-id="element-84" data-mp="2y21pl7lp2s5w">
      <ResponsiveContainer width="50%" height={chartHeight} data-id="element-85" data-mp="331o2th1iw8ep">
        <PieChart data-id="element-86" data-mp="3qstfxog2wmlb">
          <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" strokeWidth={2} stroke="#fff" data-id="element-87" data-mp="3qsa2ol1dimdm">
            {data.map((_, i) => <Cell key={i} fill={colors[i]} data-id="element-88" data-mp="1f5g8uti7zspx" data-mpi={i} />)}
          </Pie>
          <Tooltip contentStyle={{
          fontSize: 11,
          border: '1px solid #e8eaf0',
          borderRadius: 6
        }} formatter={(v: number) => [v, 'Accounts']} data-id="element-89" data-mp="1f33t8mzxpug1" />
        </PieChart>
      </ResponsiveContainer>
      <div style={{
      display: 'flex',
      gap: 12,
      flex: 1,
      paddingLeft: 4
    }} data-id="element-90" data-mp="115f4v56ns568">
        {renderLegendCol(col1)}
        {col2.length > 0 && renderLegendCol(col2)}
      </div>
    </div>;
}